import { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { Source, Layer, useMap } from "react-map-gl/mapbox"
import type { LayerProps } from "react-map-gl/mapbox"
import area from "@turf/area"
import centroid from "@turf/centroid"
import distance from "@turf/distance"
import midpoint from "@turf/midpoint"
import type { Feature, FeatureCollection, Point, Polygon } from "geojson"
import { Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"

const PRIMARY_COLOR = "#3b82f6"

export const MeasureSurface = () => {
  const [polygons, setPolygons] = useState<Feature[]>([])
  const [currentPolygon, setCurrentPolygon] = useState<number[][] | null>(null)
  const [currentLabel, setCurrentLabel] = useState<Feature | null>(null)
  const [mousePosition, setMousePosition] = useState<number[] | null>(null)
  const { current: map } = useMap()
  const [toolbarContainer, setToolbarContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setToolbarContainer(document.getElementById("explore-map-toolbar"))
  }, [])

  const polygonLayer: LayerProps = {
    id: "measure-surface-polygon",
    type: "fill",
    paint: {
      "fill-color": PRIMARY_COLOR,
      "fill-opacity": 0.3,
    },
  }

  const polygonOutlineLayer: LayerProps = {
    id: "measure-surface-polygon-outline",
    type: "line",
    paint: {
      "line-color": PRIMARY_COLOR,
      "line-width": 2,
    },
  }

  const currentPolygonLayer: LayerProps = {
    id: "measure-surface-current-polygon",
    type: "fill",
    paint: {
      "fill-color": PRIMARY_COLOR,
      "fill-opacity": 0.2,
    },
  }

  const currentPolygonOutlineLayer: LayerProps = {
    id: "measure-surface-current-polygon-outline",
    type: "line",
    paint: {
      "line-color": PRIMARY_COLOR,
      "line-width": 2,
      "line-dasharray": [2, 2],
    },
  }

  const pointLayer: LayerProps = {
    id: "measure-surface-points",
    type: "circle",
    paint: {
      "circle-radius": 5,
      "circle-color": PRIMARY_COLOR,
      "circle-stroke-width": 2,
      "circle-stroke-color": "#fff",
    },
  }

  const labelLayer: LayerProps = {
    type: "symbol",
    layout: {
      "text-field": ["get", "label"],
      "text-size": 14,
      "text-offset": [0, 0],
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 2,
    },
  }

  const segmentLabelLayer: LayerProps = {
    type: "symbol",
    layout: {
      "text-field": ["get", "label"],
      "text-size": 14,
      "text-offset": [0, 1.5],
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 2,
    },
  }

  const formatDistance = (distInKm: number): string => {
    if (distInKm < 1) {
      return `${(distInKm * 1000).toFixed(2)} m`
    }
    return `${distInKm.toFixed(2)} km`
  }

  const computeSegmentLabels = (points: number[][], closed: boolean): Feature[] => {
    const features: Feature[] = []
    for (let i = 0; i < points.length - 1; i++) {
      const ptA: Point = { type: "Point", coordinates: points[i] }
      const ptB: Point = { type: "Point", coordinates: points[i + 1] }
      const dist = distance(ptA, ptB, { units: "kilometers" })
      const mid = midpoint(ptA, ptB)
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: mid.geometry.coordinates },
        properties: { label: formatDistance(dist) },
      })
    }
    if (closed && points.length >= 3) {
      const ptA: Point = { type: "Point", coordinates: points[points.length - 1] }
      const ptB: Point = { type: "Point", coordinates: points[0] }
      const dist = distance(ptA, ptB, { units: "kilometers" })
      const mid = midpoint(ptA, ptB)
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: mid.geometry.coordinates },
        properties: { label: formatDistance(dist) },
      })
    }
    return features
  }

  const formatArea = (areaInSquareMeters: number): string => {
    if (areaInSquareMeters < 10000) {
      return `${areaInSquareMeters.toFixed(2)} m²`
    } else {
      return `${(areaInSquareMeters / 10000).toFixed(2)} ha`
    }
  }

  const handleClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!currentPolygon) {
        setCurrentPolygon([[e.lngLat.lng, e.lngLat.lat]])
        setCurrentLabel(null)
      } else {
        const newPolygon = [...currentPolygon, [e.lngLat.lng, e.lngLat.lat]]
        setCurrentPolygon(newPolygon)

        if (newPolygon.length >= 3) {
          const closedPolygon = [...newPolygon, newPolygon[0]]
          const polygonGeometry: Polygon = {
            type: "Polygon",
            coordinates: [closedPolygon],
          }
          const polygonFeature: Feature = {
            type: "Feature",
            geometry: polygonGeometry,
            properties: {},
          }
          const areaInSquareMeters = area(polygonFeature)
          const center = centroid(polygonFeature)

          setCurrentLabel({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: center.geometry.coordinates,
            },
            properties: {
              label: formatArea(areaInSquareMeters),
            },
          })
        }
      }
    },
    [currentPolygon],
  )

  const handleDoubleClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      e.originalEvent.preventDefault()

      if (!currentPolygon || currentPolygon.length < 3) return

      const closedPolygon = [...currentPolygon, currentPolygon[0]]

      const polygonGeometry: Polygon = {
        type: "Polygon",
        coordinates: [closedPolygon],
      }

      const polygonFeature: Feature = {
        type: "Feature",
        geometry: polygonGeometry,
        properties: {},
      }

      const areaInSquareMeters = area(polygonFeature)
      const center = centroid(polygonFeature)

      const newPolygonFeature: Feature = {
        type: "Feature",
        geometry: polygonGeometry,
        properties: {
          id: `polygon-${polygons.length}`,
          label: formatArea(areaInSquareMeters),
          center: center.geometry.coordinates,
        },
      }

      setPolygons([...polygons, newPolygonFeature])
      setCurrentPolygon(null)
      setCurrentLabel(null)
    },
    [currentPolygon, polygons],
  )

  const handleReset = useCallback(() => {
    setPolygons([])
    setCurrentPolygon(null)
    setCurrentLabel(null)
    if (map) map.getCanvas().style.cursor = "crosshair"
  }, [map])

  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      handleReset()
    },
    [handleReset],
  )

  const handleMouseMove = useCallback((e: mapboxgl.MapMouseEvent) => {
    setMousePosition([e.lngLat.lng, e.lngLat.lat])
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && currentPolygon) {
        setCurrentPolygon(null)
        setCurrentLabel(null)
      }
    },
    [currentPolygon],
  )

  useEffect(() => {
    if (!map) return

    const canvas = map.getCanvas()
    canvas.style.cursor = "crosshair"
    map.on("click", handleClick)
    map.on("dblclick", handleDoubleClick)
    map.on("mousemove", handleMouseMove)
    document.addEventListener("keydown", handleKeyDown)
    canvas.addEventListener("contextmenu", handleContextMenu)

    return () => {
      map.off("click", handleClick)
      map.off("dblclick", handleDoubleClick)
      map.off("mousemove", handleMouseMove)
      document.removeEventListener("keydown", handleKeyDown)
      canvas.removeEventListener("contextmenu", handleContextMenu)
      canvas.style.cursor = ""
    }
  }, [map, currentPolygon, polygons, handleContextMenu])

  const geojson: FeatureCollection = {
    type: "FeatureCollection",
    features: polygons,
  }

  const currentPolygonGeojson: FeatureCollection =
    currentPolygon && currentPolygon.length >= 3
      ? {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: [[...currentPolygon, currentPolygon[0]]],
              },
              properties: {
                id: "current-polygon",
              },
            },
          ],
        }
      : { type: "FeatureCollection", features: [] }

  const currentPointsGeojson: FeatureCollection = currentPolygon
    ? {
        type: "FeatureCollection",
        features: currentPolygon.map((pt, i) => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: pt },
          properties: { id: `point-${i}` },
        })),
      }
    : { type: "FeatureCollection", features: [] }

  const currentLinesGeojson: FeatureCollection =
    currentPolygon && currentPolygon.length >= 2
      ? {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature" as const,
              geometry: { type: "LineString" as const, coordinates: currentPolygon },
              properties: { id: "current-lines" },
            },
          ],
        }
      : { type: "FeatureCollection", features: [] }

  const rubberBandGeojson: FeatureCollection =
    currentPolygon && currentPolygon.length >= 1 && mousePosition
      ? {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature" as const,
              geometry: {
                type: "LineString" as const,
                coordinates: [currentPolygon[currentPolygon.length - 1], mousePosition],
              },
              properties: { id: "rubber-band" },
            },
          ],
        }
      : { type: "FeatureCollection", features: [] }

  const currentLabelGeojson: FeatureCollection = currentLabel
    ? {
        type: "FeatureCollection",
        features: [currentLabel],
      }
    : { type: "FeatureCollection", features: [] }

  const currentSegmentLabelsGeojson: FeatureCollection =
    currentPolygon && currentPolygon.length >= 2
      ? {
          type: "FeatureCollection",
          features: computeSegmentLabels(currentPolygon, false),
        }
      : { type: "FeatureCollection", features: [] }

  const rubberBandLabelGeojson: FeatureCollection =
    currentPolygon && currentPolygon.length >= 1 && mousePosition
      ? (() => {
          const ptA: Point = { type: "Point", coordinates: currentPolygon[currentPolygon.length - 1] }
          const ptB: Point = { type: "Point", coordinates: mousePosition }
          const dist = distance(ptA, ptB, { units: "kilometers" })
          const mid = midpoint(ptA, ptB)
          return {
            type: "FeatureCollection" as const,
            features: [
              {
                type: "Feature" as const,
                geometry: { type: "Point" as const, coordinates: mid.geometry.coordinates },
                properties: { label: formatDistance(dist) },
              },
            ],
          }
        })()
      : { type: "FeatureCollection", features: [] }

  const finalizedSegmentLabelsGeojson: FeatureCollection = {
    type: "FeatureCollection",
    features: polygons.flatMap((polygon) => {
      const coords = (polygon.geometry as Polygon).coordinates[0]
      const points = coords.slice(0, -1) // remove closing duplicate
      return computeSegmentLabels(points, true)
    }),
  }

  const labelGeojson: FeatureCollection = {
    type: "FeatureCollection",
    features: polygons.map((polygon) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: polygon.properties?.center,
      },
      properties: {
        id: `label-${polygon.properties?.id}`,
        label: polygon.properties?.label,
      },
    })),
  }

  return (
    <>
      <Source id="surface-source" type="geojson" data={geojson}>
        <Layer {...polygonLayer} />
        <Layer {...polygonOutlineLayer} />
      </Source>
      <Source id="current-surface-source" type="geojson" data={currentPolygonGeojson}>
        <Layer {...currentPolygonLayer} />
        <Layer {...currentPolygonOutlineLayer} />
      </Source>
      <Source id="current-surface-points" type="geojson" data={currentPointsGeojson}>
        <Layer {...pointLayer} />
      </Source>
      <Source id="current-surface-lines" type="geojson" data={currentLinesGeojson}>
        <Layer {...currentPolygonOutlineLayer} id="measure-surface-current-lines" />
      </Source>
      <Source id="rubber-band-source" type="geojson" data={rubberBandGeojson}>
        <Layer {...currentPolygonOutlineLayer} id="measure-surface-rubber-band" />
      </Source>
      <Source id="surface-label-source" type="geojson" data={labelGeojson}>
        <Layer id="surface-label" {...labelLayer} />
      </Source>
      <Source id="current-surface-label-source" type="geojson" data={currentLabelGeojson}>
        <Layer id="current-surface-label" {...labelLayer} />
      </Source>
      <Source id="current-segment-labels-source" type="geojson" data={currentSegmentLabelsGeojson}>
        <Layer id="current-segment-labels" {...segmentLabelLayer} />
      </Source>
      <Source id="rubber-band-label-source" type="geojson" data={rubberBandLabelGeojson}>
        <Layer id="rubber-band-label" {...segmentLabelLayer} />
      </Source>
      <Source id="finalized-segment-labels-source" type="geojson" data={finalizedSegmentLabelsGeojson}>
        <Layer id="finalized-segment-labels" {...segmentLabelLayer} />
      </Source>
      {(polygons.length > 0 || currentPolygon !== null) &&
        toolbarContainer &&
        createPortal(
          <Button variant="destructive" size="lg" onClick={handleReset}>
            <Trash2 className="h-4 w-4 mr-2" />
            Effacer
          </Button>,
          toolbarContainer,
        )}
    </>
  )
}
